package com.writerslab

import kotlinx.browser.document
import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine

/**
 * Backend API base URL. Read from `window._wlApiBase` (set inline in
 * index.html). Empty string means same-origin.
 */
fun jsApiBase(): String =
    (window.asDynamic()._wlApiBase as? String) ?: ""

/** Open the URL in a new browser tab. */
fun jsOpenInNewTab(url: String) {
    window.open(url, "_blank", "noopener,noreferrer")
}

/**
 * Trigger a browser download of the given text as a UTF-8 .txt file.
 * Builds the file from text already fetched via the auth-aware HTTP
 * client (a plain browser fetch can't attach the bearer token).
 */
fun jsTriggerTextDownload(text: String, filename: String) {
    val blob = Blob(arrayOf(text), BlobPropertyBag(type = "text/plain;charset=utf-8"))
    val url = URL.createObjectURL(blob)
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.download = filename
    document.body?.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

/**
 * Trigger a browser download of arbitrary binary bytes (e.g. a fetched
 * mp4). The download attribute requires a same-origin href; an object
 * URL is always same-origin, so this works even when the bytes were
 * fetched cross-origin (a direct anchor to a cross-origin URL would
 * silently ignore `download` and navigate instead).
 */
fun jsTriggerBinaryDownload(bytes: ByteArray, filename: String, mime: String) {
    val url = createBlobUrl(bytes, mime)
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.download = filename
    document.body?.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

/**
 * Wrap a binary payload in a Blob and return its object URL. Caller is
 * responsible for `URL.revokeObjectURL(url)` once the URL is no longer
 * in use — otherwise the Blob lives until the page unloads (a leak,
 * not a correctness bug). Used by the teaser preview to render the
 * fetched PNG / MP4 without exposing the asset URL in the DOM.
 */
fun createBlobUrl(bytes: ByteArray, mime: String): String {
    // Kotlin/JS ByteArray maps to Int8Array; Blob accepts BufferSource,
    // so we wrap the same underlying buffer in a Uint8Array view.
    val int8 = bytes.unsafeCast<Int8Array>()
    val u8 = org.khronos.webgl.Uint8Array(int8.buffer, int8.byteOffset, int8.length)
    val blob = Blob(arrayOf(u8), BlobPropertyBag(type = mime))
    return URL.createObjectURL(blob)
}

fun revokeBlobUrl(url: String) {
    URL.revokeObjectURL(url)
}

/**
 * Open a native file picker restricted to the given extensions and return
 * the chosen [File] (or null if the user cancelled). On Android Chrome
 * setting the `accept` attribute keeps the dialog in the Files picker
 * rather than the photo picker.
 */
suspend fun pickFile(extensions: List<String>): File? = suspendCoroutine { cont ->
    val input = (document.createElement("input") as HTMLInputElement).apply {
        type = "file"
        accept = extensions.joinToString(",") { ".$it" }
        style.display = "none"
    }
    document.body?.appendChild(input)
    var resumed = false
    input.onchange = { _: Event ->
        val file = input.files?.item(0)
        input.remove()
        if (!resumed) {
            resumed = true
            cont.resume(file)
        }
    }
    input.click()
}

/** Read a file's contents as a ByteArray. */
suspend fun File.readBytesSuspend(): ByteArray = suspendCoroutine { cont ->
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result as ArrayBuffer
        // Kotlin's ByteArray maps to Int8Array on the JS side.
        cont.resume(Int8Array(buffer).unsafeCast<ByteArray>())
    }
    reader.onerror = { cont.resume(ByteArray(0)) }
    reader.readAsArrayBuffer(this@readBytesSuspend)
}
